/* /account/extension - the PathsAbroad extension, as part of the site. Students add it to Chrome with one button; it
   connects by itself to the student signed in here (app/extension.tsx) and puts a "Fill with PathsAbroad" button on
   their application pages. On a phone, the PathsAbroad app does the same inside itself. A pairing code is still here,
   folded away, for when connecting by itself doesn't work. Needs the Apply plan.
   ?installed=1: the extension opens this page when it's installed. ?connect=1: its button on a portal opened this
   page to connect; the extension closes the tab once it's connected. */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { WEB_ROUTES, type PairResponse } from '@pathabroad/core/browser';
import { useExtension } from '../app/extension';
import { useT } from '../app/i18n';
import { Link, useLocation } from '../app/nav';
import { useMe } from '../app/session';
import { useErrorHandler, useNow } from '../app/hooks';
import { BrandMark, Icon } from '../app/icons';
import { Alert, Button, Chip, CopyButton, Live, PageHeader, Section } from '../app/ui';
import { api, ApiError } from '../lib/api';
import { clock } from '../lib/format';
import { deviceKind, phoneStore, STORES, type Device } from '../lib/stores';

export function Extension() {
  const { t } = useT();
  const me = useMe();
  return (
    <>
      <PageHeader title={t('ext.title')} lead={t('ext.lead')} back={{ to: WEB_ROUTES.account, label: t('nav.account') }} />
      {me.plans.apply ? <Status /> : <Upsell />}
      <Section title={t('ext.howTitle')} id="how">
        <ol className="steps card">
          <li><p>{t('ext.how1')}</p></li>
          <li><p>{t('ext.how2')}</p></li>
          <li><p>{t('ext.how3')}</p></li>
        </ol>
      </Section>
      <Section title={t('ext.rulesTitle')} id="rules">
        <ul className="ticks">
          <li>{t('ext.rule1')}</li>
          <li>{t('ext.ruleEmail')}</li>
          <li>{t('ext.rule2')}</li>
          <li>{t('ext.rule3')}</li>
          <li>{t('ext.rule4')}</li>
          <li>{t('ext.rule5')}</li>
        </ul>
        <p className="fine mt">{t('ext.notAffiliated')}</p>
      </Section>
      {me.plans.apply && (
        <Section title={t('ext.codeTitle')} id="code">
          <details className="card disclose">
            <summary>{t('ext.codeLead')}</summary>
            <Pairing />
          </details>
        </Section>
      )}
    </>
  );
}

function Upsell() {
  const { t } = useT();
  return (
    <div className="card upsell">
      <span className="tile tile--indigo"><Icon.puzzle /></span>
      <div>
        <h2 className="upsell__title">{t('acct.withApply')}</h2>
        <p>{t('ext.upsell')}</p>
        <Link to={WEB_ROUTES.plans} className="btn btn--primary btn--small">{t('inbox.seePlans')}</Link>
      </div>
    </div>
  );
}

/* where things stand, on a night display: what the student does next on this device */
function Status() {
  const { t } = useT();
  const ext = useExtension();
  const { query } = useLocation();
  const [device] = useState<Device>(() => deviceKind());
  const connected = ext.state === 'connected';

  let title: string, text: string, action: ReactNode = null;
  if (device === 'app') {
    title = t('ext.inAppTitle'); text = t('ext.inAppText');
  } else if (device === 'phone') {
    title = t('ext.phoneTitle'); text = t('ext.phoneText');
    action = <AppButtons />;
  } else if (connected) {
    title = t('ext.onTitle');
    text = query.get('connect') === '1' ? `${t('ext.onBack')} ${t('ext.onText')}` : query.get('installed') === '1' ? `${t('ext.onInstalled')} ${t('ext.onText')}` : t('ext.onText');
  } else if (ext.state === 'checking' || ext.state === 'connecting') {
    title = t(ext.state === 'checking' ? 'ext.checking' : 'ext.connecting'); text = t('ext.how2');
  } else if (ext.state === 'error') {
    title = t('ext.errTitle'); text = t('ext.errText');
    action = <Button onClick={ext.retry} className="hero__cta">{t('ext.retry')}</Button>;
  } else if (device === 'other') {
    title = t('ext.otherTitle'); text = t('ext.otherText');
  } else {
    title = t('ext.addTitle'); text = t('ext.addText');
    action = STORES.chrome ? (
      <a className="btn btn--primary hero__cta" href={STORES.chrome} target="_blank" rel="noopener noreferrer">
        {t('ext.addButton')}<span className="sr-only"> {t('common.opensNew')}</span>
      </a>
    ) : <p className="fine ext-hero__soon">{t('ext.addSoon')}</p>;
  }

  return (
    <section className="hero night ext-hero" aria-labelledby="ext-status" data-state={device === 'chromium' ? ext.state : device}>
      <div className="ext-hero__top">
        <BrandMark gid="pa-mark-ext" className="ext-hero__mark" />
        {device === 'chromium' && connected && <Chip tone="green">{t('ext.connected')}</Chip>}
        {device === 'chromium' && (ext.state === 'checking' || ext.state === 'connecting') && <span className="spinner" aria-hidden="true" />}
      </div>
      <h2 className="hero__title" id="ext-status">{title}</h2>
      <p className="hero__text">{text}</p>
      {action}
      {device === 'chromium' && !connected && ext.state === 'missing' && <p className="fine ext-hero__fine">{t('ext.browsers')}</p>}
      {(device === 'chromium' || device === 'app') && <Preview />}
      <Live className="sr-only">{connected ? t('ext.onTitle') : ext.state === 'error' ? t('ext.errTitle') : ''}</Live>
    </section>
  );
}

/* the App Store and Google Play, this phone's store first */
function AppButtons() {
  const { t } = useT();
  const first = phoneStore();
  const links = [
    { id: 'ios', href: STORES.ios, label: t('ext.appStore') },
    { id: 'android', href: STORES.android, label: t('ext.playStore') },
  ].filter(l => l.href).sort((a, b) => (a.id === first ? -1 : b.id === first ? 1 : 0));
  if (!links.length) return <p className="fine ext-hero__soon">{t('ext.appSoon')}</p>;
  return (
    <div className="actions">
      {links.map((l, i) => (
        <a key={l.id} className={`btn ${i === 0 ? 'btn--primary hero__cta' : 'btn--secondary'}`} href={l.href} target="_blank" rel="noopener noreferrer">
          {l.label}<span className="sr-only"> {t('common.opensNew')}</span>
        </a>
      ))}
    </div>
  );
}

/* what the student will see on their application page: their answers outlined, and the button in the corner */
function Preview() {
  const { t } = useT();
  return (
    <div className="ext-demo" aria-hidden="true">
      <div className="ext-demo__page" dir="ltr">
        <span className="ext-demo__title" />
        <span className="ext-demo__label" /><span className="ext-demo__field is-filled"><i>Amina</i></span>
        <span className="ext-demo__label" /><span className="ext-demo__field is-filled"><i>Okafor</i></span>
        <span className="ext-demo__label" /><span className="ext-demo__field is-check"><i>14 / 03 / 2008</i></span>
        <span className="ext-demo__label" /><span className="ext-demo__field" />
      </div>
      <div className="ext-demo__pill">
        <BrandMark gid="pa-mark-demo" className="ext-demo__mark" />
        <span>{t('ext.fillButton')}</span>
      </div>
    </div>
  );
}

/* the fallback: a one-use code (10 minutes), typed into the extension's panel */
function Pairing() {
  const { t } = useT();
  const handle = useErrorHandler();
  const [pair, setPair] = useState<{ code: string; expiresAt: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [announce, setAnnounce] = useState('');
  const codeRef = useRef<HTMLParagraphElement>(null);
  const now = useNow(!!pair);
  const left = pair ? Math.max(0, Math.ceil((pair.expiresAt - now) / 1000)) : 0;
  const expired = !!pair && left === 0;

  /* screen readers hear the code, then a reminder at one minute left and when it runs out (not every second) */
  useEffect(() => {
    if (!pair) return;
    if (left === 60) setAnnounce(t('ext.minuteLeft'));
    if (left === 0) setAnnounce(t('ext.expired'));
  }, [left, pair, t]);

  const getCode = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<PairResponse>('/api/extension/pair');
      setPair({ code: r.code, expiresAt: Date.now() + Math.max(0, r.expires_in) * 1000 });
      setAnnounce('');
    } catch (e) {
      if (e instanceof ApiError && e.code === 'plan_required') { setError(t('err.plan_required')); return; }
      const msg = handle(e);
      if (msg) setError(msg);
    } finally { setBusy(false); }
  };

  return (
    <>
      <ol className="steps mt">
        <li>
          <p>{t('ext.step2')}</p>
          {!pair || expired ? (
            <Button busy={busy} onClick={() => void getCode()} className="mt-sm">{pair ? t('ext.newCode') : t('ext.getCode')}</Button>
          ) : null}
          {error && <div className="mt-sm"><Alert>{error}</Alert></div>}
        </li>
        <li>
          <p>{t('ext.step3')}</p>
          {pair && (
            <div className={expired ? 'paircode is-expired' : 'paircode'}>
              <p className="paircode__label" id="pair-label">{t('ext.code')}</p>
              <p className="paircode__code" ref={codeRef} dir="ltr" aria-labelledby="pair-label">{pair.code}</p>
              {expired ? <p className="text-danger">{t('ext.expired')}</p> : (
                <>
                  <p className="paircode__timer" role="timer" aria-live="off">{t('ext.expiresIn', { time: clock(left) })}</p>
                  <CopyButton text={pair.code} label={t('ext.copy')} selectRef={codeRef} />
                  <p className="hint">{t('ext.oneUse')}</p>
                </>
              )}
            </div>
          )}
        </li>
      </ol>
      <Live className="sr-only">{announce}</Live>
    </>
  );
}
